import { MessageBarType } from '@fluentui/react';
import { RequestEvents } from '@v2/api/utils';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { appEventBus } from 'shared/infrastructure/app-event-bus';
import React from "react";

interface BlockingActionContext {
  setContent: (content: string | React.ReactElement) => void;
}

interface Message {
  content: string | null;
  type: MessageBarType;
}

export interface DialogButton {
  key: string;
  text: string;
  primary?: boolean;
}

export interface DialogOptions {
  title?: string;
  content: string;
  buttons: DialogButton[];
}

interface DialogTask {
  options: DialogOptions & {
    buttons: (DialogButton & { onClick: () => void })[];
  };
}

export class AppState {
  private _isBlockingActionRunning = false;
  private _blockingActionContent: string | React.ReactElement = '';
  private runningApiRequestCount = 0;
  dialogContent: React.ReactNode | null = null;
  private currentDialogResolve: ((result: string | null) => void) | null = null;

  errorMessage: Message | null = null;

  constructor() {
    makeAutoObservable(this, {
      errorMessage: observable.ref,
    });

    appEventBus.addListener(RequestEvents.apiRequestStarted, () => {
      this.runningApiRequestCount++;
    });

    appEventBus.addListener(RequestEvents.apiRequestDone, () => {
      this.runningApiRequestCount--;
    });
  }

  get isBlockingActionRunning() {
    return this._isBlockingActionRunning || this.dialogContent !== null;
  }

  get blockingActionContent() {
    return this._blockingActionContent;
  }

  async runBlockingAction<T>(func: (_context: BlockingActionContext) => Promise<T>) {
    this._isBlockingActionRunning = true;
    try {
      const context: BlockingActionContext = {
        setContent: (content: string | React.ReactElement) => (this._blockingActionContent = content),
      };
      return await func(context);
    } finally {
      runInAction(() => {
        this._isBlockingActionRunning = false;
        this._blockingActionContent = '';
      });
    }
  }

  showDialog(renderDialog: (setResult: (result: any) => void) => React.ReactNode): Promise<any> {
    return new Promise((resolve) => {
      this.dialogContent = renderDialog((result) => {
        resolve(result);
        this.dialogContent = null;
      });
    });
  }

  closeDialog() {
    if (this.currentDialogResolve) {
      this.currentDialogResolve(null);
    }
    this.dialogContent = null;
    this.currentDialogResolve = null;
  }

  showErrorMessage(content: string) {
    this.errorMessage = {
      content,
      type: MessageBarType.error,
    };
  }

  showWarningMessage(content: string) {
    this.errorMessage = {
      content,
      type: MessageBarType.warning,
    };
  }

  showInfoMessage(content: string) {
    this.errorMessage = {
      content,
      type: MessageBarType.info,
    };
  }

  get hasRunningApiRequests() {
    return this.runningApiRequestCount > 0;
  }
}

export const appState = new AppState();
export const useAppState = () => appState;
